import { useEffect, useState } from 'react'

export type ProposalLoadingStage = 'REVIEWING_PALETTE' | 'SEARCHING_CATALOG' | 'GENERATING_RECOMMENDATIONS'

export interface ProposalLoadingStageConfig {
  stage: ProposalLoadingStage
  headline: string
  progressPercent: number
  startsAtMs: number
  fillDurationMs: number
}

// The last stage stops at 94% on purpose: the bar has to stay visibly unfinished
// until the proposal response actually lands, however long that takes.
// The newlines are the mock's explicit line breaks and only render because
// .proposal-loading-headline sets `white-space: pre-line`.
// Timings are paced against the ~10.6s observed on POST /quiz/submit/last-step:
// the final stage eases toward 94% over 4.2s so the bar keeps moving instead of
// sitting frozen for the remainder of the wait.
export const PROPOSAL_LOADING_STAGES: ProposalLoadingStageConfig[] = [
  {
    stage: 'REVIEWING_PALETTE',
    headline: 'Reviewing\nyour palette',
    progressPercent: 24,
    startsAtMs: 0,
    fillDurationMs: 600,
  },
  {
    stage: 'SEARCHING_CATALOG',
    headline: 'Searching\ndesign catalog',
    progressPercent: 74,
    startsAtMs: 2800,
    fillDurationMs: 2000,
  },
  {
    stage: 'GENERATING_RECOMMENDATIONS',
    headline: 'Generating\nyour recommendations',
    progressPercent: 94,
    startsAtMs: 5600,
    fillDurationMs: 4200,
  },
]

export const getProposalLoadingStageConfig = (stage: ProposalLoadingStage): ProposalLoadingStageConfig =>
  PROPOSAL_LOADING_STAGES.find((config) => config.stage === stage) || PROPOSAL_LOADING_STAGES[0]

export const useTimedProposalLoadingStage = (): ProposalLoadingStage => {
  const [stageIndex, setStageIndex] = useState(0)

  useEffect(() => {
    const timers = PROPOSAL_LOADING_STAGES.slice(1).map((config, index) =>
      window.setTimeout(() => setStageIndex(index + 1), config.startsAtMs)
    )

    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [])

  return PROPOSAL_LOADING_STAGES[stageIndex].stage
}
